import { IntelligenceFixture } from '../../types';
import { PLACEHOLDER_WHY_THIS_RESULT, PLACEHOLDER_CONFIDENCE_REASON } from '../placeholderCopy';
import { FIXTURE_UPDATED_AT } from '../updatedAt';

// Both callout panels label every row `Lorem Ipsum <sub-label>` in the file — the bold run is
// placeholder, the span beside it is real (`589:27997`, `589:28002`, `589:28007`, `589:28014`,
// `589:28019`, `589:28024`). The bold run is kept verbatim rather than replaced with authored copy.
const PLACEHOLDER_DRIVER_LABEL = 'Lorem Ipsum';

/* Every EGS term raises a score that IS an expectation gap, so each is a blocker. The direction is
   the registry's factor set, read off `governed_formula_expression` -- not `riseMeans`, which
   transcribes the green `+6 pts` the file paints on this tile's delta chip. PLA-8815. */
const BLOCKS = 'blocker' as const;

export const experienceValueCardFixtures: Record<string, IntelligenceFixture> = {
  current_predicted_customer_value: {
    cardKey: 'current_predicted_customer_value',
    state: { kind: 'ok' },
    updatedAt: FIXTURE_UPDATED_AT,
    // The card's three tiles, from `figma-spec.md`'s document-order dump of `589:27692` and
    // corroborated live on the scene graph (`589:27760` reads "↑ $800 (6.2% pp)"). Every figure
    // here replaces an invented one — the card used to print 412.60 / 468.90 / 13.6%, none of
    // which the file draws. The dump also fixes the ORDER: Eligible Customers leads.
    metrics: [
      {
        key: 'eligible_customers',
        label: 'Eligible Customers',
        value: 64800,
        format: 'count',
        // The same 64,800 and the same ↑ 1,250 Customer Engagement prints, but this card prints
        // them as `(3.1% pp)` where that one prints `(0.2% pp)`. Neither reconciles, so each card
        // keeps the figure its own screen shows rather than one of them being normalised away.
        delta: {
          value: 1250,
          unit: 'count',
          direction: 'up',
          sentiment: 'positive',
          secondary: { value: 3.1, unit: 'pp' }
        }
      },
      {
        key: 'current_clv',
        label: 'Current CLV — Net Margin',
        value: 1240,
        format: 'currency',
        delta: {
          value: 800,
          unit: 'currency',
          direction: 'up',
          sentiment: 'positive',
          secondary: { value: 6.2, unit: 'pp' }
        }
      },
      {
        key: 'predicted_clv',
        label: 'Predicted CLV — Net Margin',
        value: 1480,
        format: 'currency',
        delta: {
          value: 800,
          unit: 'currency',
          direction: 'up',
          sentiment: 'positive',
          secondary: { value: 8.4, unit: 'pp' }
        }
      }
    ],
    evidence: {
      // The panel is headed CALCULATION & EVIDENCE and states how the card is computed; it is not
      // the value breakdown with share bars this fixture used to invent.
      label: 'CALCULATION & EVIDENCE',
      rows: [
        {
          label: 'Value basis',
          value:
            'Net Margin selected; fallback order is Gross Margin, Revenue, then Approved Proxy.'
        },
        {
          label: 'Current CLV',
          value: 'Trailing 12-month approved value minus attributable costs.'
        },
        {
          label: 'Predicted CLV',
          value:
            'Expected next-12-month value × retention probability minus expected approved costs.'
        },
        {
          label: 'Minimum history',
          value:
            'Show Insufficient Data below 90 days of reliable history or when mandatory inputs are missing.'
        }
        // Model evidence is NOT a fifth row here: the card draws it as its own bordered panel below
        // the two callouts, so it lives in the visuals below and the grid holds the four the file
        // grids together.
      ]
    },
    confidence: {
      score: 68,
      band: 'medium',
      // `589:28058` reads `Reason: Lorem Ipsum …` — the file has not authored this card's reason.
      reasons: [PLACEHOLDER_CONFIDENCE_REASON]
    },
    // The band draws exactly one `Why this result?` box (`div.reason` `589:27988`, Top 4208) and it
    // belongs to the sentiment card. This card's box is not drawn, so its body stays placeholder
    // rather than carrying prose nothing in the file authored.
    whyThisResult: PLACEHOLDER_WHY_THIS_RESULT
  },

  customer_sentiment_expectations: {
    cardKey: 'customer_sentiment_expectations',
    state: { kind: 'ok' },
    updatedAt: FIXTURE_UPDATED_AT,
    // Three tiles, in the document-order the dump gives them: Sentiment Score, Expectation Gap
    // Score, Customers with Expectation Gap. The first two used to be absent entirely.
    metrics: [
      {
        key: 'sentiment_score',
        label: 'Sentiment Score',
        value: 68,
        format: 'points',
        // UNVERIFIED hue: this tile's delta sits left of every crop taken of the row, so the
        // sentiment here is the intuitive reading (a rising sentiment score is a good result) and
        // not, like the two tiles beside it, the colour the file paints.
        delta: { value: 4, unit: 'points', direction: 'up', sentiment: 'positive' }
      },
      {
        key: 'expectation_gap_score',
        label: 'Expectation Gap Score',
        // O-COM-021's own factor means, UAT `generic` 2026-09-08, strongest first. Two of the
        // formula's five terms have columns. Both tone `blocker` off the FACTOR SET's polarity: the
        // score is an expectation gap and a rise in it is bad, whatever hue the file paints the
        // delta chip below. PLA-8815.
        factors: [
          {
            key: 'egs_mean_delayed_attempts',
            label: 'Failed expectation events',
            value: 1.209838,
            tone: BLOCKS
          },
          {
            key: 'egs_mean_negative_signals',
            label: 'Negative sentiment',
            value: 0.125065,
            tone: BLOCKS
          }
        ],
        value: 34,
        // The file paints this `↑ 6 pts` GREEN, though a widening expectation gap reads as a bad
        // result. Same rule as the tile beside it: follow the drawn hue rather than the intuition.
        format: 'points',
        delta: { value: 6, unit: 'points', direction: 'up', sentiment: 'positive' }
      },
      {
        key: 'expectation_gap',
        // The tile counts customers, it does not print the gap as a rate: `589:27901` labels it
        // "Customers with Expectation Gap" over `589:27902`'s "8,920".
        label: 'Customers with Expectation Gap',
        value: 8920,
        format: 'count',
        delta: {
          value: 1250,
          unit: 'count',
          direction: 'down',
          // `589:27903` paints "↓ 1,250 (3.1% pp)" in Froly #EE6475 — the file reads the fall as a
          // bad result, so the tile follows the drawn hue rather than the intuition that fewer
          // customers in the gap is an improvement.
          sentiment: 'negative',
          secondary: { value: 3.1, unit: 'pp' }
        }
      },
      /* PLA-8828: the catalogue's O-REV-010, bound now that every catalogue metric with a card gets
         a binding. `null` and not a figure: the endpoint answers 404 `40415` for it on UAT
         (2026-09-18), so no reading has ever been observed. The file draws no tile for this metric
         either, so there is not even a design mock to transcribe. */
      {
        key: 'reversal_engagement_impact',
        label: 'Reversal Impact on Engagement',
        value: null,
        format: 'points'
      }
    ],
    confidence: {
      // `589:27913` "Confidence: 82% · High", corroborated by the in-card badge `589:28057`
      // "High confidence · 82%".
      score: 82,
      band: 'high',
      // `589:28055` reads `Reason: Lorem Ipsum …`.
      reasons: [PLACEHOLDER_CONFIDENCE_REASON]
    },
    evidence: {
      label: 'SUPPORTING EVIDENCE',
      // The five `div.source` tiles of `589:27967`'s row, read left to right (`589:27968`,
      // `589:27972`, `589:27976`, `589:27980`, `589:27984`). No tile carries a share bar — the
      // second line is a description of the source, not a proportion of the evidence.
      rows: [
        { label: 'Surveys / NPS', value: '18,240 responses · 86% confidence' },
        { label: 'Complaints', value: '3,480 cases · severity and status' },
        { label: 'Support interactions', value: '12,860 permitted notes' },
        { label: 'Reward / fulfillment', value: 'Delay, failure and callback events' },
        { label: 'Social / MarTech', value: 'Legally permitted text only' }
      ]
    },
    // Verbatim from `589:27991`, the body of the band's one `Why this result?` box. This copy IS
    // authored in the file — it is not the Lorem Ipsum the `Reason:` lines carry.
    whyThisResult:
      'Sentiment improved because successful reward fulfillment and resolved support interactions increased. The remaining expectation gap is mainly driven by delayed rewards and unresolved complaints.'
  }
};

// The sub-blocks the design draws below the metric row of Customer Sentiment & Expectations.
// `IntelligenceFixture` has no field for a band split or a driver ranking and no endpoint shape
// exists yet to model one against, so they sit beside the payload they decorate; the section
// supplies colour, share and formatting, which are presentation, not data.
export const experienceValueVisuals = {
  /* Card 1's own panel, drawn below the two callouts rather than as a row of the evidence grid above
     them. Only its TITLE is left: the body was the same Lorem Ipsum the `Reason:` lines carry and it
     rendered on the card with no condition on it at all, so the section draws the panel empty until
     a body arrives off the wire. PLA-8815. */
  modelEvidence: { label: 'Model evidence' },
  /* The CLV bands, their legend medians, both captions, the predicted subtitle and its three stats
     are gone from here because they rendered as readings: four band counts totalling the design's
     own 64,800, four `$` medians on a metric that carries no currency column, `Trailing 12 months` /
     `Next 12 months` naming a window the response does not call one, `76% confidence` from no model,
     and `↓ 4 pts` / `↑ 9 pts` / `Expected retention 83%` — two period movements and a forecast on a
     seam with no previous period at all. The same goes for the sentiment ring's 68, its `Positive`
     band, its `Previous period: 64` and its three-band split, which O-COM-020 answers itself in four
     classes and negative-dominant; and for the five expectation-gap driver points, which O-COM-021
     has no per-driver column for. Each block keeps its container in the section. PLA-8815. */
  // The two `call good` / `call bad` panels each card draws. Both cards carry the same six rows —
  // read independently on card 1 (`589:28026` / `589:28041`) and card 2 (`589:27992` /
  // `589:28009`), which agree value for value.
  topPositiveDrivers: [
    {
      rank: 1,
      label: PLACEHOLDER_DRIVER_LABEL,
      subLabel: 'Positive observed contribution',
      value: '+6.8 pts'
    },
    {
      rank: 2,
      label: PLACEHOLDER_DRIVER_LABEL,
      subLabel: 'Positive observed contribution',
      value: '+4.1 pts'
    },
    {
      rank: 3,
      label: PLACEHOLDER_DRIVER_LABEL,
      subLabel: 'Positive observed contribution',
      value: '+2.9 pts'
    }
  ],
  topBlockers: [
    {
      rank: 1,
      label: PLACEHOLDER_DRIVER_LABEL,
      subLabel: '8,900 customers affected',
      value: '−5.2 pts'
    },
    {
      rank: 2,
      label: PLACEHOLDER_DRIVER_LABEL,
      subLabel: '7,450 customers affected',
      value: '−3.8 pts'
    },
    {
      rank: 3,
      label: PLACEHOLDER_DRIVER_LABEL,
      subLabel: '6,100 customers affected',
      value: '−2.6 pts'
    }
  ]
};
